import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDate } from "../../../../../helper/helper";
import { Images } from "../../../../../utils/imageConstants";
import ImageZoomModal from "../../../../../components/common/ImageZoomModal";
import type { GalleryCardModel } from "../../../../../utils/galleryApiMap";

type GalleryItem = GalleryCardModel;

const GalleryDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const galleryItem = (location.state as { galleryItem?: GalleryItem })?.galleryItem;

  const images = useMemo(() => {
    if (!galleryItem) return [];
    if (galleryItem.images && galleryItem.images.length > 0) {
      return galleryItem.images;
    }
    if (galleryItem.imageUrl && galleryItem.imageUrl !== Images.gallery_match_image) {
      return [galleryItem.imageUrl];
    }
    return Array.from(
      { length: Math.max(1, galleryItem.photoCount) },
      () => Images.gallery_match_image,
    );
  }, [galleryItem]);

  const heroSrc = images[0];
  const gridImages = images.length > 1 ? images.slice(1) : [];

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // If no gallery item in state, redirect back to gallery / photos list
  useEffect(() => {
    if (!galleryItem) {
      const path = location.pathname;
      let back = "/tournaments/tournaments-details";
      if (path.includes("/tournaments/team-info/gallery-details")) {
        back = "/tournaments/team-info";
      } else if (path.includes("/leagues-home") && path.includes("gallery-details")) {
        back = "/leagues-home";
      }
      navigate(back, { replace: true });
    }
  }, [galleryItem, navigate, location.pathname]);

  if (!galleryItem) {
    return null;
  }

  const dateLine =
    galleryItem.date && galleryItem.date.trim()
      ? `${formatDate(galleryItem.date)} | ${galleryItem.photoCount} ${galleryItem.photoCount === 1 ? "Photo" : "Photos"}`
      : `${galleryItem.photoCount} ${galleryItem.photoCount === 1 ? "Photo" : "Photos"}`;

  return (
    <div className="gallery-details-wrapper">
      <article className="gallery-details-post" aria-label="Gallery album">
        {heroSrc ? (
          <button
            type="button"
            className="gallery-details-post-cover"
            onClick={() => handleImageClick(0)}
          >
            <img src={heroSrc} alt="" className="gallery-details-post-cover-img" />
          </button>
        ) : null}
        <div className="gallery-details-post-body">
          <h2 className="gallery-details-post-title">{galleryItem.match}</h2>
          <p className="gallery-details-post-meta">{dateLine}</p>
        </div>
      </article>

      {gridImages.length > 0 ? (
        <div className="gallery-details-grid">
          {gridImages.map((imageSrc, index) => {
            const globalIndex = index + 1;
            return (
              <div
                key={globalIndex}
                className="gallery-details-item"
                onClick={() => handleImageClick(globalIndex)}
              >
                <div className="gallery-details-img-wrapper">
                  <img
                    src={imageSrc}
                    alt={`${galleryItem.match} - Photo ${globalIndex + 1}`}
                    className="gallery-details-image"
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {isModalOpen && images.length > 0 && (
        <ImageZoomModal
          images={images}
          initialIndex={selectedImageIndex}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          altText={`${galleryItem.match} - Photo`}
        />
      )}
    </div>
  );
};

export default GalleryDetails;

